import { useEffect, useRef, useState } from "react"

import {
  easeOutCubic,
  hasAnimatableNumber,
  interpolateNumbers,
  numericValues,
} from "../utils/count-up"

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

type UseCountUpOptions = {
  /** The pre-formatted target string (e.g. "3,120", "6.0%", "260 MEUR"). */
  value: string
  /** Total animation duration in ms. Defaults to 800ms (DESIGN.md §6). */
  duration?: number
}

/**
 * Animates the numeric tokens inside a pre-formatted display string from 0 (on
 * first mount) — or from the previous value when `value` changes — up to the
 * target, preserving thousands separators, decimals and any surrounding chrome.
 *
 * Mirrors `useTypewriter`'s discipline:
 *   - requestAnimationFrame with ease-out, cleaned up on unmount/value-change.
 *   - Honours `prefers-reduced-motion: reduce` (renders the final value
 *     instantly, no animation).
 *   - Only animates on initial mount and on genuine value changes — never on
 *     unrelated re-renders (DESIGN.md §6: "Only animate on initial mount, not
 *     on re-renders").
 *
 * Non-numeric strings (e.g. "Moderate") are returned verbatim with no animation.
 */
export function useCountUp({ value, duration = 800 }: UseCountUpOptions): string {
  const animatable = hasAnimatableNumber(value)
  const reduced = prefersReducedMotion()
  const skip = !animatable || reduced || duration <= 0

  const [display, setDisplay] = useState<string>(value)
  const frameRef = useRef<number | null>(null)
  // The numeric values we last settled on — the "from" basis for the next run.
  // Seeded to the initial target only when we skip animating it.
  const fromRef = useRef<number[]>(skip ? numericValues(value) : [])
  // The target string the rAF loop has fully settled on. Set ONLY on
  // completion (not at animation start) so that an interrupted run — e.g. React
  // StrictMode's mount→cleanup→mount, or a remount — re-animates instead of
  // parking on an intermediate frame. Until a run completes this stays at its
  // previous value, so the effect won't early-return on an unfinished target.
  const settledRef = useRef<string | null>(null)

  useEffect(() => {
    // Already fully settled on this exact target (a re-render with no value
    // change, after the animation completed): keep it, don't replay.
    if (settledRef.current === value) {
      // Guarantee the rendered string matches the settled target even if a
      // prior cleanup left an intermediate frame on screen.
      setDisplay(value)
      return
    }

    if (skip) {
      setDisplay(value)
      fromRef.current = numericValues(value)
      settledRef.current = value
      return
    }

    const from = fromRef.current.length > 0 ? fromRef.current : []
    const targets = numericValues(value)
    let start: number | null = null

    const step = (now: number) => {
      if (start === null) start = now
      const t = Math.min((now - start) / duration, 1)
      setDisplay(interpolateNumbers(value, from, easeOutCubic(t)))
      if (t < 1) {
        frameRef.current = requestAnimationFrame(step)
      } else {
        // Convergence guarantee: land exactly on the target and record it as
        // settled so the "from" basis for the next change is the true value.
        setDisplay(value)
        fromRef.current = targets
        settledRef.current = value
        frameRef.current = null
      }
    }

    setDisplay(interpolateNumbers(value, from, 0))
    frameRef.current = requestAnimationFrame(step)

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
    }
  }, [value, duration, skip])

  return display
}
